"use client";

import { MdOutlineAdminPanelSettings } from "react-icons/md";
import { CiCircleQuestion, CiLogout, CiSettings } from "react-icons/ci";
import Link from "next/link";

import RailTooltip from "@/components/nav/RailTooltip";
import { sheetRowClass } from "@/components/nav/nav-styles";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getInitials } from "@/components/ui/UserAvatar";
import { hardSignOut } from "@/lib/auth/clientSignOut";

const ICON = "h-5 w-5 shrink-0 text-gray-400";

/**
 * The signed-in person's menu behind their avatar: settings, help, the
 * superUser console when they have one, and sign out. On the rail it opens to
 * the right; in the mobile header it drops down.
 */
export default function AccountMenu({ user, side = "right" }) {
  if (!user) return null;

  const items = [
    user.workspaceId && {
      href: "/settings",
      label: "Settings",
      Icon: CiSettings,
    },
    { href: "/help", label: "Help Center", Icon: CiCircleQuestion },
    user.superUser &&
      !user.isImpersonated && {
        href: "/admin/superUser",
        label: "SuperUser console",
        Icon: MdOutlineAdminPanelSettings,
      },
  ].filter(Boolean);

  return (
    <Popover>
      <RailTooltip label="Account" side={side}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Account menu"
            className="flex h-11 w-11 items-center justify-center rounded-lg transition-colors hover:bg-stone-200/50 data-[state=open]:bg-stone-200/50"
          >
            <Avatar className="h-8 w-8">
              <AvatarImage src={user.image || undefined} alt="" />
              <AvatarFallback className="bg-gray-700 text-xs font-semibold text-white">
                {getInitials(user.name, user.email)}
              </AvatarFallback>
            </Avatar>
          </button>
        </PopoverTrigger>
      </RailTooltip>
      <PopoverContent
        side={side}
        align="end"
        sideOffset={8}
        className="w-64 rounded-xl border-stone-200/80 p-2"
      >
        <div className="px-3 pb-2 pt-1">
          <div className="truncate text-sm font-semibold text-gray-900">
            {user.name || user.email}
          </div>
          {user.name && user.email && (
            <div className="truncate text-xs text-gray-500">{user.email}</div>
          )}
        </div>
        <div className="border-t border-gray-100 pt-1">
          {items.map(({ href, label, Icon }) => (
            <Link key={href} href={href} className={sheetRowClass(false)}>
              <Icon className={ICON} />
              {label}
            </Link>
          ))}
          <button
            type="button"
            onClick={() => hardSignOut("/")}
            className={`${sheetRowClass(false)} w-full`}
          >
            <CiLogout className={ICON} />
            Sign out
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
